import { tryOnScopeDispose } from '@vueuse/core'

import { fontPreviewUrl, uprightWeights, type GoogleFontItem } from '../lib/brandAppFonts'

const CATALOG_URL = 'https://www.googleapis.com/webfonts/v1/webfonts'
const CACHE_KEY = 'brandApp.googleFonts'
const CACHE_TTL_MS = 24 * 60 * 60 * 1000

type CatalogStatus = 'idle' | 'loading' | 'ready' | 'error'

const catalog = shallowRef<GoogleFontItem[]>([])
const status = ref<CatalogStatus>('idle')
const previewLinks = new Map<string, { link: HTMLLinkElement; users: number }>()

const isFontItem = (value: unknown): value is GoogleFontItem => {
  const item = value as GoogleFontItem
  return (
    typeof item?.family === 'string' &&
    typeof item.category === 'string' &&
    Array.isArray(item.variants) &&
    typeof item.files === 'object' &&
    item.files !== null
  )
}

const trimItem = (item: GoogleFontItem): GoogleFontItem => {
  const variants = item.variants.filter((variant) => uprightWeights([variant]).length > 0)

  return {
    family: item.family,
    category: item.category,
    variants,
    files: Object.fromEntries(variants.map((variant) => [variant, item.files[variant]]).filter(([, url]) => url))
  }
}

const readCache = (): GoogleFontItem[] | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const cached = JSON.parse(raw) as { savedAt?: number; items?: unknown[] }
    if (typeof cached.savedAt !== 'number' || Date.now() - cached.savedAt > CACHE_TTL_MS) return null

    return Array.isArray(cached.items) ? cached.items.filter(isFontItem) : null
  } catch {
    return null
  }
}

const writeCache = (items: GoogleFontItem[]) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), items }))
  } catch {
    return
  }
}

const fetchCatalog = async (): Promise<GoogleFontItem[]> => {
  const key = import.meta.env.VITE_GOOGLE_FONTS_API_KEY
  if (!key) throw new Error('google-fonts-key-missing')

  const params = new URLSearchParams({ key, sort: 'popularity', capability: 'WOFF2' })
  const response = await fetch(`${CATALOG_URL}?${params.toString()}`)
  if (!response.ok) throw new Error(`google-fonts-${response.status}`)
  const body = (await response.json()) as { items?: unknown[] }

  return (body.items ?? []).filter(isFontItem).map(trimItem)
}

export default function useGoogleFonts() {
  const load = async () => {
    if (status.value === 'loading' || status.value === 'ready') return

    const cached = readCache()
    if (cached?.length) {
      catalog.value = cached
      status.value = 'ready'
      return
    }

    status.value = 'loading'
    try {
      const items = await fetchCatalog()
      catalog.value = items
      writeCache(items)
      status.value = 'ready'
    } catch (error) {
      console.error(error)
      status.value = 'error'
    }
  }

  const usedPreviews = new Set<string>()

  const loadPreview = (family: string) => {
    if (usedPreviews.has(family) || typeof document === 'undefined') return
    usedPreviews.add(family)

    const existing = previewLinks.get(family)
    if (existing) {
      existing.users += 1
      return
    }

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = fontPreviewUrl(family, family)
    document.head.appendChild(link)
    previewLinks.set(family, { link, users: 1 })
  }

  const disposePreviews = () => {
    usedPreviews.forEach((family) => {
      const entry = previewLinks.get(family)
      if (!entry) return
      entry.users -= 1
      if (entry.users > 0) return
      entry.link.remove()
      previewLinks.delete(family)
    })
    usedPreviews.clear()
  }

  tryOnScopeDispose(disposePreviews)

  return { catalog, status, load, loadPreview, disposePreviews }
}
